/**
 * UpdatePrompt - Notifies user when new version is available
 *
 * Features:
 * - Shows toast/banner when update detected
 * - "Update Now" button to reload with new version
 * - "Later" button to dismiss
 * - Automatically activates new service worker on update
 */

import { useTranslation } from 'react-i18next';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { X, RefreshCw } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export function UpdatePrompt() {
  const { t } = useTranslation();
  const {
    offlineReady: [offlineReady, setOfflineReady],
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegistered(registration) {
      console.log('✅ Service Worker registered');

      // Check for updates every hour
      if (registration) {
        setInterval(() => {
          registration.update();
        }, 60 * 60 * 1000); // 1 hour
      }
    },
    onRegisterError(error) {
      console.error('❌ Service Worker registration error:', error);
    },
  });

  const close = () => {
    setOfflineReady(false);
    setNeedRefresh(false);
  };

  const handleUpdate = () => {
    updateServiceWorker(true); // true = reload page after update
  };

  return (
    <AnimatePresence>
      {(offlineReady || needRefresh) && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed bottom-4 right-4 z-[9999] max-w-md"
        >
          <div className="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded-lg shadow-2xl p-4">
            <div className="flex items-start gap-3">
              {/* Icon */}
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center">
                  <RefreshCw className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                  {offlineReady ? t('App.PWA.UpdatePrompt.TitleOfflineReady') : t('App.PWA.UpdatePrompt.TitleNewVersion')}
                </h3>
                <p className="mt-1 text-sm text-neutral-600 dark:text-neutral-400">
                  {offlineReady
                    ? t('App.PWA.UpdatePrompt.BodyOfflineReady')
                    : t('App.PWA.UpdatePrompt.BodyNewVersion')}
                </p>

                {/* Actions */}
                <div className="mt-3 flex gap-2">
                  {needRefresh && (
                    <button
                      onClick={handleUpdate}
                      className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-lg transition-colors"
                    >
                      {t('App.PWA.UpdatePrompt.UpdateNow')}
                    </button>
                  )}
                  <button
                    onClick={close}
                    className="px-4 py-2 bg-neutral-100 dark:bg-neutral-800 hover:bg-neutral-200 dark:hover:bg-neutral-700 text-neutral-700 dark:text-neutral-300 text-sm font-medium rounded-lg transition-colors"
                  >
                    {needRefresh ? t('App.PWA.UpdatePrompt.Later') : t('App.PWA.UpdatePrompt.Close')}
                  </button>
                </div>
              </div>

              {/* Close button */}
              <button
                onClick={close}
                className="flex-shrink-0 p-1 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-full transition-colors"
                aria-label={t('App.PWA.UpdatePrompt.CloseAriaLabel')}
              >
                <X className="w-4 h-4 text-neutral-500 dark:text-neutral-400" />
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
